import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, FormControl, InputLabel, Select, MenuItem,
  Checkbox, Tooltip, ToggleButtonGroup, ToggleButton, Chip, CircularProgress,
} from '@mui/material';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { ScopeDTO, FunctionalityDTO, ScopeFunctionalityDTO } from 'axiosApi/models';
import Spinner from '../Spinner';

const TYPE_LABELS: Record<number, string> = { 1: 'API', 2: 'UI', 3: 'Rule' };
const TYPE_COLORS: Record<number, 'default' | 'primary' | 'secondary'> = { 1: 'primary', 2: 'secondary', 3: 'default' };

// Key for the matrix lookup map
const key = (functionalityId: number, scopeId: number) => `${functionalityId}:${scopeId}`;

const ScopeFunctionalityMatrix = () => {
  const api: Api = useApi();
  const toast = useToastMessageQueue();

  const [scopes, setScopes] = useState<ScopeDTO[]>([]);
  const [functionalities, setFunctionalities] = useState<FunctionalityDTO[]>([]);
  const [modules, setModules] = useState<string[]>([]);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');

  // Map of "functionalityId:scopeId" -> ScopeFunctionalityDTO (holds the assignment id for deletion)
  const [matrix, setMatrix] = useState<Map<string, ScopeFunctionalityDTO>>(new Map());

  const [loadingScopes, setLoadingScopes] = useState(false);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [toggling, setToggling] = useState<Set<string>>(new Set());

  // Load all scopes once
  useEffect(() => {
    setLoadingScopes(true);
    api.employeeApi.apiVversionScopeAllGet('1', 1, 500, {})
      .then(res => setScopes((res.data?.data?.queryResult || []).sort((a, b) => (a.scope || '').localeCompare(b.scope || ''))))
      .catch(e => handleAPIError(e, toast, {}))
      .finally(() => setLoadingScopes(false));
  }, []);

  // Load module list once
  useEffect(() => {
    api.employeeApi.apiVversionFunctionalityAllGet('1', 1, 500, undefined, undefined, {})
      .then(res => {
        const mods = [...new Set(
          (res.data?.data?.queryResult || [])
            .map((f: FunctionalityDTO) => f.module)
            .filter((m): m is string => Boolean(m))
        )].sort() as string[];
        setModules(mods);
      })
      .catch(() => {});
  }, []);

  // Load functionalities whenever module or type filter changes
  const loadFunctionalities = useCallback(async () => {
    if (!selectedModule) {
      setFunctionalities([]);
      setMatrix(new Map());
      return;
    }
    setLoadingMatrix(true);
    try {
      const res = await api.employeeApi.apiVversionFunctionalityAllGet('1', 1, 200, selectedModule, undefined, {});
      let items: FunctionalityDTO[] = res.data?.data?.queryResult || [];
      if (selectedType) {
        items = items.filter(f => String(f.type) === selectedType);
      }
      setFunctionalities(items);

      // Parallel-load scope assignments for every functionality in this module
      const assignments = await Promise.all(
        items.map(f =>
          api.employeeApi.apiVversionFunctionalityFunctionalityIdScopesGet(f.id!, '1', {})
            .then(r => ({ functionalityId: f.id!, rows: (r.data?.data?.queryResult || []) as ScopeFunctionalityDTO[] }))
            .catch(() => ({ functionalityId: f.id!, rows: [] as ScopeFunctionalityDTO[] }))
        )
      );

      const newMatrix = new Map<string, ScopeFunctionalityDTO>();
      for (const { functionalityId, rows } of assignments) {
        for (const row of rows) {
          newMatrix.set(key(functionalityId, row.idScope!), row);
        }
      }
      setMatrix(newMatrix);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoadingMatrix(false);
    }
  }, [selectedModule, selectedType]);

  useEffect(() => { loadFunctionalities(); }, [loadFunctionalities]);

  const handleToggle = async (f: FunctionalityDTO, s: ScopeDTO) => {
    const k = key(f.id!, s.id!);
    if (toggling.has(k)) return;

    const existing = matrix.get(k);
    setToggling(prev => new Set(prev).add(k));

    try {
      if (existing) {
        await api.employeeApi.apiVversionFunctionalityScopeIdDelete(existing.id!, '1', {});
        setMatrix(prev => {
          const next = new Map(prev);
          next.delete(k);
          return next;
        });
      } else {
        const res = await api.employeeApi.apiVversionFunctionalityScopePost('1', {
          idScope: s.id!,
          idFunctionality: f.id!,
        }, {});
        const created: ScopeFunctionalityDTO = res.data?.data || { idScope: s.id, idFunctionality: f.id };
        setMatrix(prev => new Map(prev).set(k, created));
      }
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setToggling(prev => {
        const next = new Set(prev);
        next.delete(k);
        return next;
      });
    }
  };

  const shortLabel = (identifier: string) => {
    const parts = identifier.split('.');
    return parts.slice(-2).join('.');
  };

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" sx={{ mb: 1 }}>
          Scope × Functionality Matrix
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Rows = scopes, columns = functionalities. A checked cell means the scope grants access to that functionality.
          Select a module to load its functionalities.
        </Typography>

        {/* Filters */}
        <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Module</InputLabel>
            <Select
              label="Module"
              value={selectedModule}
              onChange={e => setSelectedModule(e.target.value as string)}
            >
              {modules.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            size="small"
            value={selectedType}
            exclusive
            onChange={(_, v) => setSelectedType(v ?? '')}
          >
            <ToggleButton value="">All</ToggleButton>
            <ToggleButton value="1">API</ToggleButton>
            <ToggleButton value="2">UI</ToggleButton>
            <ToggleButton value="3">Rule</ToggleButton>
          </ToggleButtonGroup>

          {loadingMatrix && <CircularProgress size={20} />}
        </Box>

        {!selectedModule && (
          <Typography color="text.secondary" variant="body2">
            Select a module above to display the matrix.
          </Typography>
        )}

        {selectedModule && !loadingMatrix && functionalities.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            No functionalities found for the selected filters.
          </Typography>
        )}

        {selectedModule && (loadingScopes ? <Spinner /> : (
          <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
            <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  {/* Sticky top-left corner */}
                  <th style={{
                    position: 'sticky', left: 0, top: 0, zIndex: 3,
                    background: '#fff', minWidth: 180, padding: '8px 12px',
                    borderBottom: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                    textAlign: 'left', fontSize: 12, color: '#666',
                  }}>
                    Scope \ Functionality
                  </th>
                  {functionalities.map(f => (
                    <th key={f.id} style={{
                      position: 'sticky', top: 0, zIndex: 2,
                      background: '#fff', minWidth: 56, maxWidth: 56,
                      padding: '6px 4px', borderBottom: '2px solid #e0e0e0',
                      borderRight: '1px solid #f0f0f0',
                      writingMode: 'vertical-rl', textOrientation: 'mixed',
                      height: 140,
                    }}>
                      <Tooltip title={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{f.identifier}</Typography>
                          <Typography variant="caption">{f.name}</Typography>
                          {f.route && <Typography variant="caption" display="block" fontFamily="monospace">{f.httpMethod} {f.route}</Typography>}
                        </Box>
                      } arrow>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ writingMode: 'horizontal-tb' }}>
                            <Chip
                              label={TYPE_LABELS[f.type as number] || '?'}
                              size="small"
                              color={TYPE_COLORS[f.type as number] || 'default'}
                              sx={{ fontSize: 9, height: 16 }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10, fontFamily: 'monospace',
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              maxHeight: 110, display: 'block',
                            }}
                          >
                            {shortLabel(f.identifier || '')}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scopes.map((s, si) => (
                  <tr key={s.id} style={{ background: si % 2 === 0 ? '#fafafa' : '#fff' }}>
                    <td style={{
                      position: 'sticky', left: 0, zIndex: 1,
                      background: si % 2 === 0 ? '#fafafa' : '#fff',
                      padding: '2px 12px', borderRight: '1px solid #e0e0e0',
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap',
                    }}>
                      {s.scope}
                    </td>
                    {functionalities.map(f => {
                      const k = key(f.id!, s.id!);
                      const checked = matrix.has(k);
                      const busy = toggling.has(k);
                      return (
                        <td key={f.id} style={{
                          textAlign: 'center', padding: 0,
                          borderRight: '1px solid #f0f0f0',
                          borderBottom: '1px solid #f0f0f0',
                        }}>
                          {busy
                            ? <CircularProgress size={16} />
                            : (
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() => handleToggle(f, s)}
                                sx={{ p: 0.5 }}
                              />
                            )
                          }
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default ScopeFunctionalityMatrix;
